import { CaretLeft, CaretRight, Star } from '@phosphor-icons/react'
import clsx from 'clsx'
import { EmblaCarouselType } from 'embla-carousel'
import Autoplay from 'embla-carousel-autoplay'
import useEmblaCarousel from 'embla-carousel-react'
import Image from 'next/image'
import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'
import { PageInfo } from '@/src/@types/models'
import { TestimonalsBlock } from '@/src/@types/statamic'
import { getExternalReviews } from '@/src/AdminApiClient'
import Button from '@/src/components/core/Button'
import ConditionalWrapper from '@/src/components/core/ConditionalWrapper'
import { formatGerman } from '@/src/components/core/lib/date'
import ShowMore from '@/src/components/core/ShowMore'
import Title from '@/src/components/elements/Title'
import { Block } from '@/src/lib/PageBuilder'

export function getTitle({ title }: TestimonalsBlock) {
  return title
}

export async function getStaticProps({
  title,
  feedback_types,
  locations,
  all_feedbacks_link,
}: TestimonalsBlock) {
  let testimonials = await getExternalReviews(
    feedback_types?.map((type) => type.value),
    locations?.map((location) => location.value)
  )
  if (testimonials.length < 2) {
    testimonials = await getExternalReviews(
      feedback_types?.map((type) => type.value)
    )
  }
  return { title, all_feedbacks_link, testimonials: testimonials.slice(0, 50) }
}

export default function Component({
  title,
  all_feedbacks_link,
  testimonials,
  blocks,
  index,
  pageInfo,
}: Awaited<ReturnType<typeof getStaticProps>> & {
  blocks: Block[]
  index: number
  pageInfo: PageInfo
}) {
  const removeTopMargin = blocks[index - 1]?.block === 'cta_banner'

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      skipSnaps: true,
    },
    [Autoplay({ playOnInit: true, delay: 3000 })]
  )
  const [prevDisabled, setPrevDisabled] = useState(true)
  const [nextDisabled, setNextDisabled] = useState(true)

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevDisabled(!emblaApi.canScrollPrev())
    setNextDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  const prev = useCallback(() => {
    if (!emblaApi) return
    emblaApi?.plugins()?.autoplay?.stop()
    emblaApi.scrollPrev()
  }, [emblaApi])

  const next = useCallback(() => {
    if (!emblaApi) return
    emblaApi?.plugins()?.autoplay?.stop()
    emblaApi.scrollNext()
  }, [emblaApi])

  return (
    <div
      className={clsx(
        'bg-linear-to-b from-gray-50 to-gray-100 py-16 md:py-32',
        {
          '-mt-20 lg:-mt-24': removeTopMargin,
          'mt-20 md:mt-32':
            pageInfo.newSpacing && blocks[index - 1]?.block !== 'trial_lesson',
        }
      )}
    >
      <div className="mx-auto flex max-w-container items-center justify-between px-5 md:px-6">
        <Title style={2}>{title}</Title>
      </div>
      <div className="mt-6 overflow-hidden md:mt-10" ref={emblaRef}>
        <div className="flex select-none">
          {testimonials.map((testimonial) => (
            <div className="ml-5 min-w-0 flex-[0_0_calc(100%-40px-20px)] last:mr-5 min-[1328px]:first:ml-[calc((100%-1288px)/2)] min-[1328px]:last:mr-[calc((100%-1288px)/2)] min-[1750px]:flex-[0_0_20%] md:flex-[0_0_40%] lg:flex-[0_0_34%] 2xl:flex-[0_0_450px]">
              <div className="rounded-xl bg-white p-6 shadow-sm md:px-8">
                <div className="flex items-center justify-between">
                  <div className="flex space-x-4">
                    {testimonial.author_image_url &&
                      testimonial.author_image_url.includes(
                        'lh3.googleusercontent.com/a-/'
                      ) && (
                        <Image
                          sizes="48px"
                          alt={testimonial.author}
                          src={testimonial.author_image_url}
                          height={48}
                          width={48}
                          className="rounded-full object-cover"
                          unoptimized
                        />
                      )}
                    {!(
                      testimonial.author_image_url &&
                      testimonial.author_image_url.includes(
                        'lh3.googleusercontent.com/a-/'
                      )
                    ) && (
                      <p
                        className={clsx(
                          'flex size-12 items-center justify-center rounded-full text-center text-xl',
                          getColorFromString(testimonial.author)
                        )}
                      >
                        {testimonial.author.trim()[0].toUpperCase()}
                      </p>
                    )}
                    <div>
                      <p className="font-bold">{testimonial.author}</p>
                      <p className="text-gray-600">
                        {formatGerman(
                          new Date(testimonial.date),
                          'dd. MMMM yyyy'
                        )}
                      </p>
                    </div>
                  </div>
                  <ConditionalWrapper
                    when={testimonial.url}
                    wrapper={(children, url) => (
                      <Link href={url} target="_blank">
                        {children}
                      </Link>
                    )}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="size-6"
                    >
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                      <path fill="none" d="M1 1h22v22H1z" />
                    </svg>
                  </ConditionalWrapper>
                </div>
                <div className="mt-6 flex space-x-1">
                  {new Array(Math.ceil(testimonial.rating))
                    .fill(null)
                    .map((_, index) => (
                      <Star
                        key={index}
                        size={20}
                        weight="fill"
                        className="shrink-0 text-yellow-600"
                      />
                    ))}
                  {new Array(Math.floor(5 - testimonial.rating))
                    .fill(null)
                    .map((_, index) => (
                      <Star
                        key={index}
                        size={20}
                        className="shrink-0 text-yellow-600"
                      />
                    ))}
                </div>
                <ShowMore>
                  {({ clampRef, isClamped, isExpanded, setIsExpanded }) => (
                    <>
                      <p
                        ref={clampRef}
                        className={clsx('mt-4 text-gray-800', {
                          'line-clamp-4': !isExpanded,
                          'max-h-[192px] overflow-y-auto': isExpanded,
                        })}
                      >
                        {testimonial.text}
                      </p>
                      {isClamped && !isExpanded && (
                        <button
                          onClick={() => setIsExpanded(true)}
                          className="mt-4 text-sm font-bold text-purple-600 hover:text-purple-800"
                        >
                          Mehr anzeigen
                        </button>
                      )}
                    </>
                  )}
                </ShowMore>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mx-auto mt-6 flex max-w-container items-center justify-between px-5 md:mt-10 md:px-6">
        {all_feedbacks_link ? (
          <Link
            href={all_feedbacks_link}
            target="_blank"
            className="text-sm font-bold text-purple-600 md:text-base"
          >
            Alle Bewertungen anzeigen
          </Link>
        ) : (
          <div />
        )}
        {!(prevDisabled && nextDisabled) && (
          <div className="flex space-x-3">
            <Button
              size="icon-small"
              icon={<CaretLeft weight="bold" />}
              onClick={prev}
              version="gray-outlined"
              disabled={prevDisabled}
            />
            <Button
              size="icon-small"
              icon={<CaretRight weight="bold" />}
              onClick={next}
              version="gray-outlined"
              disabled={nextDisabled}
            />
          </div>
        )}
      </div>
    </div>
  )
}

const getColorFromString = (input: string) => {
  let hash = 0
  for (let i = 0; i < input.length; i++) {
    hash = hash + input.charCodeAt(i)
  }

  const index = Math.abs(hash) % FeedbackColors.length
  return FeedbackColors[index]
}

const FeedbackColors = [
  'bg-pink-600 text-white',
  'bg-red-600 text-white',
  'bg-blue-600 text-white',
  'bg-green-600 text-white',
  'bg-yellow-600 text-white',
]
